/**
 * Chat and name hygiene: masks slurs and obscenities in the four supported languages and
 * refuses names that would be unwelcome on most platforms (abuse, reserved roles).
 * Matching runs on a normalised string: lower case, common leet and homoglyphs folded,
 * separators removed. Stems are chosen to be unambiguous, so plain words stay untouched;
 * short slurs that also live inside harmless words are matched as whole words only.
 */

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', '$': 's', '!': 'i', '|': 'i', '+': 't' };
// Cyrillic letters that double as Latin look-alikes in evasion attempts
const HOMOGLYPH: Record<string, string> = { а: 'a', е: 'e', о: 'o', р: 'p', с: 'c', у: 'y', х: 'x', к: 'k', м: 'm', т: 't', в: 'b', н: 'h' };

function fold(text: string, toLatin: boolean): string {
  const s = text.toLowerCase();
  let out = '';
  for (const ch of s) {
    const l = LEET[ch];
    if (l) {
      out += l;
      continue;
    }
    if (toLatin && HOMOGLYPH[ch]) {
      out += HOMOGLYPH[ch];
      continue;
    }
    out += ch;
  }
  return out;
}

/** Latin and Cyrillic stems; each is matched as a substring of the folded, separator-free text. */
const STEMS_EN = ['fuck', 'fuk', 'shit', 'bitch', 'asshole', 'pussy', 'whore', 'slut', 'nigger', 'nigga', 'faggot', 'retard', 'hitler', 'tranny', 'wanker', 'motherfucker', 'douche'];
/** short slurs that also occur inside harmless words (Scunthorpe, Dickens, cocktail): whole words only */
const WORDS_EN = ['cunt', 'cunts', 'dick', 'dicks', 'cock', 'cocks', 'fag', 'fags', 'rape', 'rapist', 'nazi', 'nazis', 'kike', 'spic', 'chink', 'twat'];
/** transliterated Russian obscenities, whole words only (substrings would hit Asuka, baseball, ...) */
const WORDS_TRANSLIT = ['huy', 'hui', 'huilo', 'huyilo', 'huylo', 'pizda', 'pizdec', 'pizdets', 'blyat', 'blyad', 'blya', 'suka', 'suki', 'pidor', 'pidar', 'pidoras', 'mudak', 'gandon', 'ebat', 'ebal', 'zaebal', 'zaebis', 'dolboeb', 'zalupa', 'shlyuha', 'urod', 'daun'];
const STEMS_RU = ['хуй', 'хуе', 'хуя', 'хуи', 'пизд', 'ебат', 'ебан', 'ебал', 'ебну', 'ёб', 'еба', 'заеб', 'ебис', 'ебуч', 'ебл', 'бляд', 'блят', 'сука', 'суки', 'мудак', 'мудил', 'залуп', 'долбо', 'пидор', 'пидар', 'педик', 'гандон', 'гондон', 'шлюх', 'хохл', 'жид', 'чурк', 'нигер', 'негр', 'даун', 'уёб', 'уеб', 'дроч', 'манда', 'сперм', 'нацист', 'гитлер'];
const STEMS_ZH = ['他妈的', '妈的', '傻逼', '操你', '草泥马', '婊子', '妓女', '死全家', '滚蛋', '狗日', '贱人', '鸡巴'];
const STEMS_JA = ['くたばれ', 'しね', '死ね', 'クソ', '糞', 'まんこ', 'ちんこ', 'きちがい', '売女', 'ブス', 'きもい'];

/** Words that impersonate staff or the game itself; never allowed as names. */
const RESERVED = ['admin', 'administrator', 'moderator', 'mod', 'gm', 'gamemaster', 'official', 'support', 'system', 'server', 'knight', 'dev', 'developer', 'staff', 'owner', 'админ', 'администратор', 'модератор', 'модер', 'поддержка', 'разраб', 'система', 'сервер'];

function bareLatin(text: string): string {
  return fold(text, true).replace(/[^a-z]/g, '');
}

function bareCyr(text: string): string {
  return fold(text, false).replace(/[^а-яё]/g, '');
}

function latinWords(text: string): string[] {
  return fold(text, true)
    .split(/[^a-z]+/)
    .filter(Boolean);
}

export function hasProfanity(text: string): boolean {
  const lat = bareLatin(text);
  const cyr = bareCyr(text);
  for (const s of STEMS_EN) if (lat.includes(s)) return true;
  for (const s of STEMS_RU) if (cyr.includes(s)) return true;
  for (const s of STEMS_ZH) if (text.includes(s)) return true;
  for (const s of STEMS_JA) if (text.includes(s)) return true;
  const words = latinWords(text);
  for (const w of WORDS_EN) if (words.includes(w)) return true;
  for (const w of WORDS_TRANSLIT) if (words.includes(w)) return true;
  return false;
}

const TRIM = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/su;

/** Replaces each offending word with asterisks of the same length, keeping punctuation around it. */
export function maskProfanity(text: string): string {
  if (!hasProfanity(text)) return text;
  const tokens = text.split(/(\s+)/);
  let any = false;
  const out = tokens.map((tok) => {
    if (/^\s*$/.test(tok)) return tok;
    if (!hasProfanity(tok)) return tok;
    any = true;
    const m = TRIM.exec(tok);
    if (m && m[2]) return m[1] + '*'.repeat([...m[2]].length) + m[3];
    return '*'.repeat([...tok].length);
  });
  // evasion by spaces inside the word: the whole line trips the filter but no single token does
  if (!any) return '*'.repeat(Math.min(12, [...text].length));
  return out.join('');
}

export type NameVerdict = 'ok' | 'profane' | 'reserved';

export function checkName(name: string): NameVerdict {
  if (hasProfanity(name)) return 'profane';
  const lower = name.toLowerCase();
  // words of the name, letters only: "GM_Nik", "admin7" and "Mod.Kate" all expose their first word
  const words = lower.split(/[^\p{L}]+/u).filter(Boolean);
  const lat = bareLatin(name);
  const cyr = bareCyr(name);
  for (const r of RESERVED) {
    if (words.includes(r)) return 'reserved';
    const bare = /^[a-z]+$/.test(r) ? lat : cyr;
    if (bare === r) return 'reserved';
    // long reserved words glued to anything else: administrator_x, поддержка1
    if (r.length >= 5 && (lower.startsWith(r) || lower.endsWith(r))) return 'reserved';
  }
  return 'ok';
}

export function isNameAllowed(name: string): boolean {
  return checkName(name) === 'ok';
}
